/* eslint-disable no-unused-vars */
import {useDynamicEvents} from '@/composables/useDynamicEvents'
import {storeToRefs} from 'pinia'
import {useUserStore} from '@/store/user'
import useI18nExtended from '~/composables/usei18nExtended'

export interface UseCcrsFilterHandlersOptions {
  trackApplyFilter: (props: Record<string, string>) => void
  trackResetToSearch: () => void
  filter: (filters: any) => void
  setCcrsFiltersInUrl: (filters: any) => Promise<void>
  loadProducts: () => Promise<void>
  filters: Ref<any[]>
  defaultFilters: Ref<any[] | undefined>
  defaultFiltersInitialized: Ref<boolean>
  initialCcrsFilters: Ref<any[] | null>
  filtersDisabled: Ref<boolean>
  resetDisabled: Ref<boolean>
  userHasModifiedFilters: Ref<boolean>
  filtersAdjustedForZeroProducts: Ref<boolean>
  productsLoading: Ref<boolean>
  resetInProgress: Ref<boolean>
}

export const useCcrsFilterHandlers = (options: UseCcrsFilterHandlersOptions) => {
  const {
    trackApplyFilter,
    trackResetToSearch,
    filter,
    setCcrsFiltersInUrl,
    loadProducts,
    filters,
    defaultFilters,
    defaultFiltersInitialized,
    initialCcrsFilters,
    filtersDisabled,
    resetDisabled,
    userHasModifiedFilters,
    filtersAdjustedForZeroProducts,
    productsLoading,
    resetInProgress
  } = options

  const {buildDynamicFilterProps} = useDynamicEvents()
  const {showInchValues} = storeToRefs(useUserStore())
  const i18n = useI18nExtended()

  const handleApplyFilter = (appliedFilters: any) => {
    resetInProgress.value = true
    filter(appliedFilters)
    setTimeout(() => {
      handleTrackSelectedFilterParameters(filters.value)
    })

    const filtersMatchDefaults = JSON.stringify(appliedFilters) === JSON.stringify(defaultFilters.value)
    filtersDisabled.value = filtersMatchDefaults
    resetDisabled.value = filtersMatchDefaults
    userHasModifiedFilters.value = !filtersMatchDefaults
  }

  const handleTrackSelectedFilterParameters = (filters: any[]) => {
    const activeFilters = filters.filter(f => f.isActive)
    const props = activeFilters.length ? buildDynamicFilterProps(activeFilters, showInchValues.value, i18n.tv.bind(i18n)) : {}

    trackApplyFilter(props)
  }

  const handleResetFilters = () => {
    trackResetToSearch()

    productsLoading.value = true
    resetInProgress.value = true

    // Reset filters by marking non-initial filters as inactive so their URL params get removed
    const initialFilterShortNames = new Set(initialCcrsFilters.value?.map((f: any) => f.shortName) ?? [])
    const filtersToSet = filters.value?.map((f: any) => {
      if (initialFilterShortNames.has(f.shortName)) {
        return initialCcrsFilters.value?.find((init: any) => init.shortName === f.shortName && init.type === f.type) ?? f
      }
      return {...f, isActive: false, isFilteredBy: false}
    }) ?? []

    setCcrsFiltersInUrl(filtersToSet)
    filters.value = filtersToSet
    filtersDisabled.value = true
    resetDisabled.value = true
    userHasModifiedFilters.value = false
    filtersAdjustedForZeroProducts.value = false
    loadProducts()
  }

  const handleFilterChanged = () => {
    if (!defaultFiltersInitialized.value || !defaultFilters.value?.length || !filters.value?.length) {
      filtersDisabled.value = true
      return
    }

    if (filters.value.length !== defaultFilters.value.length) {
      filtersDisabled.value = false
      return
    }

    const getFilterKey = (f: any) => JSON.stringify({
      shortName: f.shortName,
      indexPath: f.indexPath,
      values: f.values,
      type: f.type,
      unitOfMeasure: f.unitOfMeasure
    })

    const currentSignatures = filters.value.map(getFilterKey).sort((a, b) => a.localeCompare(b))
    const defaultSignatures = defaultFilters.value.map(getFilterKey).sort((a, b) => a.localeCompare(b))

    filtersDisabled.value = JSON.stringify(currentSignatures) === JSON.stringify(defaultSignatures)
  }

  return {
    handleApplyFilter,
    handleResetFilters,
    handleFilterChanged
  }
}

export type UseCcrsFilterHandlers = ReturnType<typeof useCcrsFilterHandlers>
